import React, { useState, useEffect, useRef } from 'react';
import {
    Card, CardBody, Container, Form, Row, Col,
    Label, Input, FormFeedback, Spinner
} from 'reactstrap';
import { Btn, H5 } from '../../AbstractElements';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { apiPost, baseUrl, mediaUrl } from '../../Utils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useAuth } from '../../Context/AuthContext';
import { useConfig } from '../../Context/ConfigContext';
import { useNavigate } from 'react-router-dom';
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";

// Logo must be exactly this size
const LOGO_WIDTH = 300;
const LOGO_HEIGHT = 98;

interface SettingForm {
    company_name: string;
    address: string;
    contact_number: string;
    email: string;
    currency: string;
}

interface Errors {
    company_name?: string;
    address?: string;
    contact_number?: string;
    email?: string;
    currency?: string;
    logo?: string;
}

export default function AddGeneralSettings() {
    const { hasPermission } = useAuth();
    const { refreshConfig } = useConfig();
    const canAdd = hasPermission('general-settings.add');
    const navigate = useNavigate();
    const [form, setForm] = useState<SettingForm>({
        company_name: '',
        address: '',
        contact_number: '',
        email: '',
        currency: '',
    });
    const [errors, setErrors] = useState<Errors>({});
    const [saving, setSaving] = useState(false);

    // Logo state
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [existingLogo, setExistingLogo] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // No fetch Settings on mount for Add form

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const handlePhoneChange = (value: string) => {
        setForm(prev => ({ ...prev, contact_number: value }));
        setErrors(prev => ({ ...prev, contact_number: undefined }));
    };

    const validatePhoneNumber = (value: string) => {
        if (!value) {
            return "Contact number is required.";
        }
        const sanitizedValue = value.replace(/\D/g, "");
        const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);
        if (!phoneNumber || !phoneNumber.isValid()) {
            return "Invalid contact number.";
        }
        const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
        if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
            return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
        }
        return true;
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setErrors(prev => ({ ...prev, logo: 'Only image files are allowed.' }));
            setLogoFile(null);
            setLogoPreview(null);
            return;
        }

        // Validate dimensions
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            if (img.width !== LOGO_WIDTH || img.height !== LOGO_HEIGHT) {
                URL.revokeObjectURL(objectUrl);
                setErrors(prev => ({
                    ...prev,
                    logo: `Logo must be exactly ${LOGO_WIDTH}×${LOGO_HEIGHT} px. Your image is ${img.width}×${img.height} px.`
                }));
                setLogoFile(null);
                setLogoPreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                setErrors(prev => ({ ...prev, logo: undefined }));
                setLogoFile(file);
                setLogoPreview(objectUrl);
            }
        };
        img.src = objectUrl;
    };

    const validate = (): boolean => {
        const newErrors: Errors = {};
        if (!form.company_name.trim()) newErrors.company_name = 'Company name is required.';
        if (!form.address.trim()) newErrors.address = 'Address is required.';

        const phoneValidationResult = validatePhoneNumber(form.contact_number);
        if (phoneValidationResult !== true) {
            newErrors.contact_number = phoneValidationResult as string;
        }

        if (!form.email.trim()) newErrors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Enter a valid email address.';
        if (!form.currency) newErrors.currency = 'Please select a currency.';
        if (!logoFile && !errors.logo) newErrors.logo = 'Dashboard Logo is required.';
        if (errors.logo) newErrors.logo = errors.logo; // carry over dimension error
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('company_name', form.company_name);
            formData.append('address', form.address);
            formData.append('contact_number', form.contact_number);
            formData.append('email', form.email);
            formData.append('currency', form.currency);
            if (logoFile) formData.append('logo', logoFile);

            await axios.post(`${baseUrl}settings/store`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            await refreshConfig();
            toast.success('Settings saved successfully!', { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/general-settings`);
            }, 1000);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add General Settings" parent="Master Settings" title="Add General Settings" />
            <Container fluid>
                <Card className="shadow-sm">
                    <CardBody>
                        {!canAdd ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fa fa-lock fa-2x mb-2 d-block"></i>
                                You do not have permission to add General Settings.
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit} noValidate>

                                <Row className="g-3">
                                    {/* Company Name */}
                                    <Col md={6}>
                                        <Label className="fw-semibold">
                                            Company Name <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            name="company_name"
                                            value={form.company_name}
                                            onChange={handleChange}
                                            placeholder="Enter company name"
                                            invalid={!!errors.company_name}
                                            autoComplete="off"
                                        />
                                        <FormFeedback>{errors.company_name}</FormFeedback>
                                    </Col>

                                    {/* Email */}
                                    <Col md={6}>
                                        <Label className="fw-semibold">
                                            Email <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            placeholder="Enter email address"
                                            invalid={!!errors.email}
                                            autoComplete="off"
                                        />
                                        <FormFeedback>{errors.email}</FormFeedback>
                                    </Col>

                                    {/* Contact Number */}
                                    <Col md={6}>
                                        <Label className="fw-semibold">
                                            Contact Number <span className="text-danger">*</span>
                                        </Label>
                                        <PhoneInput
                                            country={"ae"}
                                            onlyCountries={["ae", "sa", "in"]}
                                            preferredCountries={["ae"]}
                                            value={form.contact_number}
                                            onChange={handlePhoneChange}
                                            inputProps={{
                                                name: "contact_number",
                                                placeholder: "Enter Contact Number",
                                                autoComplete: "none"
                                            }}
                                            inputStyle={{
                                                width: '100%',
                                                borderColor: errors.contact_number ? '#dc3545' : '#ced4da'
                                            }}
                                        />
                                        {errors.contact_number && (
                                            <FormFeedback style={{ display: 'block' }}>
                                                {errors.contact_number}
                                            </FormFeedback>
                                        )}
                                    </Col>

                                    {/* Currency */}
                                    <Col md={6}>
                                        <Label className="fw-semibold">
                                            Currency <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            name="currency"
                                            value={form.currency}
                                            onChange={handleChange}
                                            placeholder="Enter currency"
                                            invalid={!!errors.currency}
                                            autoComplete="off"
                                        />
                                        <FormFeedback>{errors.currency}</FormFeedback>
                                    </Col>

                                    {/* Address */}
                                    <Col md={12}>
                                        <Label className="fw-semibold">
                                            Address <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="textarea"
                                            name="address"
                                            value={form.address}
                                            onChange={handleChange}
                                            placeholder="Enter full address"
                                            rows={3}
                                            invalid={!!errors.address}
                                        />
                                        <FormFeedback>{errors.address}</FormFeedback>
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">
                                            Dashboard Logo Upload <span className="text-danger">*</span>{' '}
                                            <span className="text-muted fw-normal" style={{ fontSize: '12px' }}>
                                                (Exact size: {LOGO_WIDTH}×{LOGO_HEIGHT} px)
                                            </span>
                                        </Label>

                                        {/* Upload button */}
                                        <div
                                            className="rounded d-flex align-items-center justify-content-center"
                                            style={{
                                                height: '90px',
                                                cursor: 'pointer',
                                                borderWidth: '1px',
                                                borderStyle: errors.logo ? 'solid' : 'dashed',
                                                borderColor: errors.logo ? '#dc3545' : '#ced4da',
                                                backgroundColor: '#f8f9fa',
                                                transition: 'border-color 0.2s',
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            {logoPreview ? (
                                                <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '70px' }} />
                                            ) : existingLogo ? (
                                                <img src={`${mediaUrl}${existingLogo}`} alt="Current logo" style={{ maxHeight: '70px' }} />
                                            ) : (
                                                <div className="text-center text-muted">
                                                    <i className="fa fa-upload fa-lg mb-1 d-block"></i>
                                                    <small>Click to upload logo</small>
                                                </div>
                                            )}
                                        </div>

                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={handleLogoChange}
                                        />

                                        {errors.logo && (
                                            <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                                {errors.logo}
                                            </div>
                                        )}
                                        {!errors.logo && logoFile && (
                                            <div className="text-success mt-1" style={{ fontSize: '12px' }}>
                                                ✓ Logo is valid ({LOGO_WIDTH}×{LOGO_HEIGHT} px)
                                            </div>
                                        )}
                                    </Col>
                                </Row>


                                {/* Submit */}
                                <div className="mt-4 d-flex justify-content-start">
                                    <Btn color="primary" type="submit" disabled={saving}>
                                        {saving ? <> Submiting <Spinner size="sm" /></> : 'Submit'}
                                    </Btn>
                                </div>
                            </Form>
                        )}
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
